/* eslint-disable */
import { useState, useMemo } from "react";
import { Stack, Center, Loader, Text, Alert, Modal, Button, Group, Title, Card, Badge, ActionIcon } from "@mantine/core";
import { IconAlertCircle, IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { useGetRolePermissions } from "api/role/getRoles";
import { useDeleteRole } from "api/role/deleteRole";
import { useHasPermission } from "api/permission/useHasPermission";
import { useUser } from "hooks/useUser";
import { RolePermission } from "api/role/addRole";
import { buildTree, flattenTree, filterVisibleItems } from "../utils/treeUtils";
import PermissionEditor from "./PermissionEditor";
import CreateRoleModal from "./CreateRoleModal";

export default function RoleHierarchyTree() {
  const { user } = useUser();
  const organizationId = user.org?._id;

  const { data, isLoading, error } = useGetRolePermissions();
  const { mutate: deleteRole } = useDeleteRole();
  const canCreateRole = useHasPermission("role_management.create_role");

  const [selectedRoleForEdit, setSelectedRoleForEdit] = useState<RolePermission | null>(null);
  const [deleteConfirmRole, setDeleteConfirmRole] = useState<RolePermission | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());

  // Build tree structure and flatten
  const { flatItems, rolesArray } = useMemo(() => {
    if (!data?.data) return { flatItems: [], rolesArray: [] };

    const rolesData = data.data;
    const tree = buildTree(rolesData);
    const flat = flattenTree(tree);

    return { flatItems: flat, rolesArray: rolesData };
  }, [data?.data]);

  // Filter visible items based on expanded state
  const visibleItems = useMemo(
    () => filterVisibleItems(flatItems, expandedRoles),
    [flatItems, expandedRoles]
  );

  // Toggle expand/collapse
  const handleToggleExpand = (role: string) => {
    const newExpanded = new Set(expandedRoles);
    if (newExpanded.has(role)) {
      newExpanded.delete(role);
    } else {
      newExpanded.add(role);
    }
    setExpandedRoles(newExpanded);
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmRole) {
      deleteRole({ role: deleteConfirmRole.role });
      setDeleteConfirmRole(null);
    }
  };

  if (isLoading) {
    return (
      <Center style={{ height: 400 }}>
        <Loader />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error loading roles">
        Failed to load roles. Please try again.
      </Alert>
    );
  }

  if (!rolesArray || rolesArray.length === 0) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="blue" title="No roles">
        No roles found. Create a new role to get started.
      </Alert>
    );
  }

  return (
    <>
      <Stack spacing="md" style={{ position: "relative" }}>
        {/* Loading Overlay */}
        {isLoading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 20,
              borderRadius: "8px",
            }}
          >
            <Loader size="lg" />
          </div>
        )}

        <Group position="apart">
          <Title order={2}>Role Hierarchy</Title>
          {canCreateRole && (
            <Button onClick={() => setCreateModalOpen(true)} disabled={isLoading}>
              Create Role
            </Button>
          )}
        </Group>

        {/* Hierarchical role list with expand/collapse */}
        <Stack spacing="xs" style={{ pointerEvents: isLoading ? "none" : "auto" }}>
          {visibleItems.length === 0 ? (
            <Text color="dimmed" align="center" py="xl">
              No roles to display
            </Text>
          ) : (
            visibleItems.map((item) => {
              const hasChildren = item.children.length > 0;
              const isExpanded = expandedRoles.has(item.role);
              const indentPx = item.depth * 40;

              return (
                <Card
                  key={item.role}
                  shadow="sm"
                  p="md"
                  radius="md"
                  withBorder
                  style={{
                    marginLeft: `${indentPx}px`,
                  }}
                >
                  <Group position="apart" spacing="sm">
                    <Group spacing="sm" style={{ flex: 1 }}>
                      {/* Expand/collapse chevron */}
                      {hasChildren && (
                        <ActionIcon
                          size="sm"
                          variant="transparent"
                          onClick={() => handleToggleExpand(item.role)}
                          title={isExpanded ? "Collapse" : "Expand"}
                        >
                          {isExpanded ? (
                            <IconChevronDown size={18} />
                          ) : (
                            <IconChevronRight size={18} />
                          )}
                        </ActionIcon>
                      )}
                      {!hasChildren && <div style={{ width: 32 }} />}

                      {/* Role name and info */}
                      <div style={{ flex: 1 }}>
                        <Text weight={600} size="md">
                          {item.role}
                        </Text>
                        {item.depth > 0 && (
                          <Text size="xs" color="dimmed">
                            Parent: {item.parentRole}
                          </Text>
                        )}
                      </div>

                      {/* Badges */}
                      <Group spacing="xs">
                        {item.isDefault && (
                          <Badge size="sm" color="blue" variant="light">
                            Default
                          </Badge>
                        )}
                        <Badge size="sm" color="gray" variant="outline">
                          👤 {item.userCount}
                        </Badge>
                        {item.children.length > 0 && (
                          <Badge size="sm" color="cyan" variant="light">
                            {item.children.length} child{item.children.length !== 1 ? "ren" : ""}
                          </Badge>
                        )}
                      </Group>
                    </Group>

                    {/* Action buttons */}
                    <Group spacing="xs" noWrap>
                      <Button
                        size="xs"
                        variant="light"
                        color="blue"
                        onClick={() => setSelectedRoleForEdit(item as unknown as RolePermission)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="xs"
                        variant="light"
                        color="red"
                        onClick={() => setDeleteConfirmRole(item as unknown as RolePermission)}
                        disabled={(item.userCount ?? 0) > 0}
                        title={(item.userCount ?? 0) > 0 ? `Cannot delete: ${item.userCount} user${item.userCount !== 1 ? "s" : ""} assigned` : ""}
                      >
                        Delete
                      </Button>
                    </Group>
                  </Group>
                </Card>
              );
            })
          )}
        </Stack>
      </Stack>

      {/* Permission Editor Modal */}
      {selectedRoleForEdit && (
        <PermissionEditor
          role={selectedRoleForEdit}
          opened={!!selectedRoleForEdit}
          onClose={() => setSelectedRoleForEdit(null)}
          allRoles={rolesArray}
        />
      )}

      {/* Create Role Modal */}
      {rolesArray.length > 0 && organizationId && (
        <CreateRoleModal
          opened={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          roles={rolesArray}
          organizationId={organizationId}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmRole && (() => {
        const flatItem = flatItems.find((item) => item.role === deleteConfirmRole.role);
        const hasChildren = !!(flatItem && flatItem.children.length > 0);
        const hasAssignedUsers = !!(deleteConfirmRole.userCount && deleteConfirmRole.userCount > 0);
        return (
          <Modal
            opened
            onClose={() => setDeleteConfirmRole(null)}
            title="Delete Role"
            centered
          >
            <Stack spacing="md">
              <Text>
                Are you sure you want to delete the role &quot;
                <strong>{deleteConfirmRole.role}</strong>&quot;?
              </Text>
              {hasAssignedUsers && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="red"
                  title="Cannot delete role"
                >
                  This role is already assigned to {deleteConfirmRole.userCount} user{deleteConfirmRole.userCount !== 1 ? "s" : ""}. You must reassign or remove all users from this role before deleting it.
                </Alert>
              )}
              {hasChildren && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="yellow"
                  title="Has child roles"
                >
                  This role has child roles. You must reassign or delete them first.
                </Alert>
              )}
              <Group position="right">
                <Button
                  variant="default"
                  onClick={() => setDeleteConfirmRole(null)}
                >
                  Cancel
                </Button>
                <Button
                  color="red"
                  onClick={handleConfirmDelete}
                  disabled={hasChildren || hasAssignedUsers}
                >
                  Delete
                </Button>
              </Group>
            </Stack>
          </Modal>
        );
      })()}
    </>
  );
}
